import React, { useState, useEffect } from 'react';
import { useNavigate } from 'react-router-dom';
import {
  Box,
  Typography,
  Paper,
  Table,
  TableBody,
  TableCell,
  TableContainer,
  TableHead,
  TableRow,
  TablePagination,
  CircularProgress,
  Alert,
  Rating,
  Card,
  CardContent,
  Chip,
  Grid,
  TextField,
  MenuItem,
  FormControl,
  InputLabel,
  Select,
  InputAdornment,
  IconButton,
  Tooltip,
  Button,
  Dialog,
  DialogTitle,
  DialogContent,
  DialogActions,
  Snackbar
} from '@mui/material';
import {
  Search as SearchIcon,
  Clear as ClearIcon,
  FilterAlt as FilterAltIcon,
  ThumbUp as ThumbUpIcon,
  ThumbDown as ThumbDownIcon,
  SortByAlpha as SortIcon,
  Reply as ReplyIcon,
  CheckCircle as CheckCircleIcon,
  Forward as ForwardIcon,
  History as HistoryIcon
} from '@mui/icons-material';
import { DatePicker } from '@mui/x-date-pickers/DatePicker';
import { LocalizationProvider } from '@mui/x-date-pickers/LocalizationProvider';
import { AdapterDateFns } from '@mui/x-date-pickers/AdapterDateFns';
import { format, parseISO } from 'date-fns';
import feedbackService, { Feedback, FeedbackFilter, FeedbackForward, FeedbackListStats } from '../../services/feedbackService';

// Quick stats component (values are computed server-side over all feedback, not the current page)
const QuickStats: React.FC<{ stats: FeedbackListStats }> = ({ stats }) => {
  return (
    <Grid container spacing={3} sx={{ mb: 3 }}>
      <Grid size={{ xs: 12, md: 6 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Average Rating</Typography>
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              <Typography variant="h3" color="primary" sx={{ mr: 2 }}>
                {stats.average_overall_rating}
              </Typography>
              <Rating value={stats.average_overall_rating} readOnly precision={0.1} size="large" />
            </Box>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              Based on {stats.total_count} feedback submissions
            </Typography>
          </CardContent>
        </Card>
      </Grid>
      
      <Grid size={{ xs: 12, md: 6 }}>
        <Card>
          <CardContent>
            <Typography variant="h6" gutterBottom>Recommendation Rate</Typography>
            <Typography variant="h3" color="primary">
              {stats.recommendation_rate}%
            </Typography>
            <Typography variant="body2" color="text.secondary" sx={{ mt: 1 }}>
              of guests would recommend our properties
            </Typography>
          </CardContent>
        </Card>
      </Grid>
    </Grid>
  );
};

const FeedbackList: React.FC = () => {
  const navigate = useNavigate();
  const [feedback, setFeedback] = useState<Feedback[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [page, setPage] = useState(0);
  const [rowsPerPage, setRowsPerPage] = useState(10);
  const [totalCount, setTotalCount] = useState(0);
  const [listStats, setListStats] = useState<FeedbackListStats | null>(null);
  
  // Filters
  const [filters, setFilters] = useState<FeedbackFilter>({
    page: 1,
    page_size: 10,
    search: '',
    rating_min: undefined,
    rating_max: undefined,
    would_recommend: undefined,
    property: '',
    start_date: undefined,
    end_date: undefined
  });
  
  // Sorting
  const [sortField, setSortField] = useState<string>('created_at');
  const [sortDirection, setSortDirection] = useState<'asc' | 'desc'>('desc');
  
  // Reply dialog state
  const [replyDialogOpen, setReplyDialogOpen] = useState(false);
  const [selectedFeedback, setSelectedFeedback] = useState<Feedback | null>(null);
  const [replyText, setReplyText] = useState('');
  const [replySending, setReplySending] = useState(false);
  const [snackbarOpen, setSnackbarOpen] = useState(false);
  const [snackbarMessage, setSnackbarMessage] = useState('');
  const [snackbarSeverity, setSnackbarSeverity] = useState<'success' | 'error' | 'warning'>('success');
  
  // Forward dialog state
  const [forwardDialogOpen, setForwardDialogOpen] = useState(false);
  const [forwardEmail, setForwardEmail] = useState('');
  const [forwardComments, setForwardComments] = useState('');
  const [forwardSending, setForwardSending] = useState(false);
  const [showForwardHistory, setShowForwardHistory] = useState(false);
  
  // Rating filter options
  const ratingOptions = [
    { value: '', label: 'Any Rating' },
    { value: '5', label: '5 Stars' },
    { value: '4', label: '4+ Stars' },
    { value: '3', label: '3+ Stars' },
    { value: '2', label: '2+ Stars' },
    { value: '1', label: '1+ Star' }
  ];
  
  // Recommendation filter options
  const recommendOptions = [
    { value: '', label: 'All' },
    { value: 'true', label: 'Would Recommend' },
    { value: 'false', label: 'Would Not Recommend' }
  ];
  
  // Fetch feedback data
  useEffect(() => {
    const fetchFeedback = async () => {
      try {
        setLoading(true);
        
        // Prepare filter parameters
        const filterParams: FeedbackFilter = {
          ...filters,
          page: page + 1,
          page_size: rowsPerPage,
          sort: `${sortDirection === 'desc' ? '-' : ''}${sortField}`
        };
        
        const response = await feedbackService.getFeedbacks(filterParams);
        setFeedback(response.results);
        setTotalCount(response.count);
        try {
          const stats = await feedbackService.getFeedbackStats();
          setListStats(stats);
        } catch (statsErr) {
          console.error('Error loading feedback stats:', statsErr);
          setListStats(null);
        }
      } catch (err: any) {
        console.error('Error fetching feedback:', err);
        if (err.response && err.response.status === 401) {
          setError('Authentication error: You need to log in again to view this data.');
        } else {
          setError(`Failed to load feedback data: ${err.message || 'Unknown error'}`);
        }
      } finally {
        setLoading(false);
      }
    };
    
    fetchFeedback();
  }, [page, rowsPerPage, filters, sortField, sortDirection]);
  
  // Handle pagination
  const handleChangePage = (event: unknown, newPage: number) => {
    setPage(newPage);
  };
  
  const handleChangeRowsPerPage = (event: React.ChangeEvent<HTMLInputElement>) => {
    setRowsPerPage(parseInt(event.target.value, 10));
    setPage(0);
  };
  
  // Handle filter changes
  const handleFilterChange = (field: keyof FeedbackFilter, value: any) => {
    setFilters((prev: FeedbackFilter) => ({ ...prev, [field]: value }));
    setPage(0); // Reset to first page when filter changes
  };
  
  // Handle search input
  const handleSearchChange = (event: React.ChangeEvent<HTMLInputElement>) => {
    handleFilterChange('search', event.target.value);
  };
  
  // Handle rating filter
  const handleRatingChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value as string;
    if (value === '') {
      handleFilterChange('rating_min', undefined);
    } else {
      handleFilterChange('rating_min', parseInt(value, 10));
    }
  };
  
  // Handle recommendation filter
  const handleRecommendChange = (event: React.ChangeEvent<{ value: unknown }>) => {
    const value = event.target.value as string;
    if (value === '') {
      handleFilterChange('would_recommend', undefined);
    } else {
      handleFilterChange('would_recommend', value === 'true');
    }
  };
  
  // Handle date filters
  const handleStartDateChange = (date: Date | null) => {
    if (date) {
      handleFilterChange('start_date', format(date, 'yyyy-MM-dd'));
    } else {
      handleFilterChange('start_date', undefined);
    }
  };
  
  const handleEndDateChange = (date: Date | null) => {
    if (date) {
      handleFilterChange('end_date', format(date, 'yyyy-MM-dd'));
    } else {
      handleFilterChange('end_date', undefined);
    }
  };
  
  // Clear all filters
  const handleClearFilters = () => {
    setFilters({
      page: 1,
      page_size: rowsPerPage,
      search: '',
      rating_min: undefined,
      rating_max: undefined,
      would_recommend: undefined,
      property: '',
      start_date: undefined,
      end_date: undefined
    });
    setPage(0);
  };
  
  // Handle sorting
  const handleSort = (field: string) => {
    if (field === sortField) {
      // Toggle direction if clicking the same field
      setSortDirection(prev => (prev === 'asc' ? 'desc' : 'asc'));
    } else {
      // Default to descending for a new field
      setSortField(field);
      setSortDirection('desc');
    }
  };
  
  // Format date helper
  const formatDate = (dateString: string) => {
    try {
      return format(parseISO(dateString), 'dd MMM yyyy');
    } catch (error) {
      return dateString;
    }
  };
  
  // Reply handlers
  const handleOpenReplyDialog = (feedbackItem: Feedback) => {
    setSelectedFeedback(feedbackItem);
    setReplyText(feedbackItem.reply || '');
    setReplyDialogOpen(true);
  };
  
  const handleCloseReplyDialog = () => {
    setReplyDialogOpen(false);
    setSelectedFeedback(null);
    setReplyText('');
  };
  
  const handleSendReply = async () => {
    if (!selectedFeedback || !replyText.trim()) {
      setSnackbarMessage('Please enter a reply message');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }
    
    try {
      setReplySending(true);
      const updatedFeedback = await feedbackService.replyToFeedback(selectedFeedback.id, replyText);
      
      // Update the feedback list with the updated item
      setFeedback(prev => prev.map(item => 
        item.id === updatedFeedback.id ? updatedFeedback : item
      ));
      
      setSnackbarMessage('Reply sent successfully to customer email');
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      handleCloseReplyDialog();
    } catch (err: any) {
      console.error('Error sending reply:', err);
      setSnackbarMessage(err.response?.data?.message || 'Failed to send reply');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setReplySending(false);
    }
  };
  
  const handleCloseSnackbar = () => {
    setSnackbarOpen(false);
  };
  
  // Forward handlers
  const handleOpenForwardDialog = (feedbackItem: Feedback) => {
    setSelectedFeedback(feedbackItem);
    setForwardEmail('');
    setForwardComments('');
    setShowForwardHistory(false);
    setForwardDialogOpen(true);
  };
  
  const handleCloseForwardDialog = () => {
    setForwardDialogOpen(false);
    setSelectedFeedback(null);
    setForwardEmail('');
    setForwardComments('');
    setShowForwardHistory(false);
  };
  
  const handleSendForward = async () => {
    if (!selectedFeedback || !forwardEmail.trim()) {
      setSnackbarMessage('Please enter a department email address');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }
    
    // Basic email validation
    const emailRegex = /^[^\s@]+@[^\s@]+\.[^\s@]+$/;
    if (!emailRegex.test(forwardEmail)) {
      setSnackbarMessage('Please enter a valid email address');
      setSnackbarSeverity('warning');
      setSnackbarOpen(true);
      return;
    }
    
    try {
      setForwardSending(true);
      await feedbackService.forwardFeedback(selectedFeedback.id, forwardEmail, forwardComments);
      
      // Refresh feedback list to get updated forwards count
      const filterParams: FeedbackFilter = {
        ...filters,
        page: page + 1,
        page_size: rowsPerPage,
        sort: `${sortDirection === 'desc' ? '-' : ''}${sortField}`
      };
      const response = await feedbackService.getFeedbacks(filterParams);
      setFeedback(response.results);
      
      setSnackbarMessage(`Feedback forwarded successfully to ${forwardEmail}`);
      setSnackbarSeverity('success');
      setSnackbarOpen(true);
      handleCloseForwardDialog();
    } catch (err: any) {
      console.error('Error forwarding feedback:', err);
      setSnackbarMessage(err.response?.data?.message || 'Failed to forward feedback');
      setSnackbarSeverity('error');
      setSnackbarOpen(true);
    } finally {
      setForwardSending(false);
    }
  };
  
  return (
    <Box>
      <Typography variant="h4" component="h1" gutterBottom>
        Guest Feedback
      </Typography>
      
      {error && (
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      )}
      
      {/* Quick Stats */}
      {!loading && listStats && listStats.total_count > 0 && <QuickStats stats={listStats} />}
      
      {/* Filters */}
      <Paper sx={{ p: 2, mb: 3 }}>
        <Grid container spacing={2} alignItems="center">
          <Grid size={{ xs: 12, md: 4 }}>
            <TextField
              label="Search"
              value={filters.search}
              onChange={handleSearchChange}
              fullWidth
              placeholder="Guest name, property, comments..."
              InputProps={{
                startAdornment: (
                  <InputAdornment position="start">
                    <SearchIcon />
                  </InputAdornment>
                ),
                endAdornment: filters.search ? (
                  <InputAdornment position="end">
                    <IconButton size="small" onClick={() => handleFilterChange('search', '')}>
                      <ClearIcon />
                    </IconButton>
                  </InputAdornment>
                ) : null
              }}
            />
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Rating</InputLabel>
              <Select
                value={filters.rating_min?.toString() || ''}
                onChange={handleRatingChange as any}
                label="Rating"
              >
                {ratingOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <Grid size={{ xs: 12, sm: 6, md: 2 }}>
            <FormControl fullWidth>
              <InputLabel>Recommendation</InputLabel>
              <Select
                value={(filters.would_recommend === undefined ? '' : filters.would_recommend.toString())}
                onChange={handleRecommendChange as any}
                label="Recommendation"
              >
                {recommendOptions.map(option => (
                  <MenuItem key={option.value} value={option.value}>
                    {option.label}
                  </MenuItem>
                ))}
              </Select>
            </FormControl>
          </Grid>
          
          <LocalizationProvider dateAdapter={AdapterDateFns}>
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <DatePicker
                label="From Date"
                value={filters.start_date ? new Date(filters.start_date) : null}
                onChange={handleStartDateChange}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
            
            <Grid size={{ xs: 12, sm: 6, md: 2 }}>
              <DatePicker
                label="To Date"
                value={filters.end_date ? new Date(filters.end_date) : null}
                onChange={handleEndDateChange}
                slotProps={{ textField: { fullWidth: true } }}
              />
            </Grid>
          </LocalizationProvider>
          
          <Grid size={{ xs: 12, md: 2 }} sx={{ display: 'flex', justifyContent: 'flex-end' }}>
            <Button
              variant="outlined"
              color="inherit"
              startIcon={<ClearIcon />}
              onClick={handleClearFilters}
              disabled={!filters.search && 
                !filters.rating_min && 
                filters.would_recommend === undefined && 
                !filters.start_date && 
                !filters.end_date}
            >
              Clear All
            </Button>
          </Grid>
        </Grid>
      </Paper>
      
      {/* Feedback Table */}
      <Paper>
        <TableContainer>
          <Table size="small">
            <TableHead>
              <TableRow>
                <TableCell 
                  onClick={() => handleSort('guest_name')}
                  sx={{ cursor: 'pointer', width: '12%' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Guest
                    {sortField === 'guest_name' && (
                      <SortIcon 
                        fontSize="small" 
                        sx={{ ml: 0.5, transform: sortDirection === 'desc' ? 'rotate(180deg)' : 'none' }} 
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell 
                  onClick={() => handleSort('property_name')}
                  sx={{ cursor: 'pointer', width: '15%' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Property
                    {sortField === 'property_name' && (
                      <SortIcon 
                        fontSize="small" 
                        sx={{ ml: 0.5, transform: sortDirection === 'desc' ? 'rotate(180deg)' : 'none' }} 
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell 
                  onClick={() => handleSort('created_at')}
                  sx={{ cursor: 'pointer', width: '10%' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center' }}>
                    Date
                    {sortField === 'created_at' && (
                      <SortIcon 
                        fontSize="small" 
                        sx={{ ml: 0.5, transform: sortDirection === 'desc' ? 'rotate(180deg)' : 'none' }} 
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell 
                  align="center"
                  onClick={() => handleSort('overall_rating')}
                  sx={{ cursor: 'pointer', width: '10%' }}
                >
                  <Box sx={{ display: 'flex', alignItems: 'center', justifyContent: 'center' }}>
                    Overall
                    {sortField === 'overall_rating' && (
                      <SortIcon 
                        fontSize="small" 
                        sx={{ ml: 0.5, transform: sortDirection === 'desc' ? 'rotate(180deg)' : 'none' }} 
                      />
                    )}
                  </Box>
                </TableCell>
                <TableCell align="center" sx={{ width: '18%' }}>
                  <Tooltip title="Cleanliness | Service | Food | Value">
                    <Typography variant="caption">Details</Typography>
                  </Tooltip>
                </TableCell>
                <TableCell align="center" sx={{ width: '8%' }}>
                  <Tooltip title="Would Recommend">
                    <ThumbUpIcon fontSize="small" />
                  </Tooltip>
                </TableCell>
                <TableCell sx={{ width: '20%' }}>Comments</TableCell>
                <TableCell align="center" sx={{ width: '7%' }}>Actions</TableCell>
              </TableRow>
            </TableHead>
            <TableBody>
              {loading ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                    <CircularProgress size={24} />
                  </TableCell>
                </TableRow>
              ) : feedback.length === 0 ? (
                <TableRow>
                  <TableCell colSpan={8} align="center" sx={{ py: 3 }}>
                    No feedback data available.
                  </TableCell>
                </TableRow>
              ) : (
                feedback.map((item) => (
                  <TableRow 
                    key={item.id} 
                    hover
                    onClick={() => navigate(`/feedback-details/${item.id}`)}
                    sx={{ cursor: 'pointer' }}
                  >
                    <TableCell>{item.guest_name}</TableCell>
                    <TableCell>{item.property_name}</TableCell>
                    <TableCell>{formatDate(item.created_at)}</TableCell>
                    <TableCell align="center">
                      <Rating value={item.overall_rating} readOnly size="small" />
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Tooltip 
                        title={
                          <Box>
                            <Box>C: {item.cleanliness_rating}/5</Box>
                            <Box>S: {item.service_rating}/5</Box>
                            <Box>F: {item.food_rating}/5</Box>
                            <Box>V: {item.value_rating}/5</Box>
                          </Box>
                        }
                        arrow
                      >
                        <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center', flexWrap: 'wrap' }}>
                          <Rating value={item.cleanliness_rating} readOnly size="small" max={5} sx={{ fontSize: '0.875rem' }} />
                          <Rating value={item.service_rating} readOnly size="small" max={5} sx={{ fontSize: '0.875rem' }} />
                          <Rating value={item.food_rating} readOnly size="small" max={5} sx={{ fontSize: '0.875rem' }} />
                          <Rating value={item.value_rating} readOnly size="small" max={5} sx={{ fontSize: '0.875rem' }} />
                        </Box>
                      </Tooltip>
                    </TableCell>
                    <TableCell align="center">
                      {item.would_recommend ? (
                        <Tooltip title="Would Recommend">
                          <ThumbUpIcon fontSize="small" color="success" />
                        </Tooltip>
                      ) : (
                        <Tooltip title="Would Not Recommend">
                          <ThumbDownIcon fontSize="small" color="error" />
                        </Tooltip>
                      )}
                    </TableCell>
                    <TableCell>
                      {item.comments ? (
                        <Tooltip title={item.comments} arrow>
                          <Typography
                            variant="body2"
                            sx={{
                              maxWidth: 200,
                              overflow: 'hidden',
                              textOverflow: 'ellipsis',
                              whiteSpace: 'nowrap'
                            }}
                          >
                            {item.comments}
                          </Typography>
                        </Tooltip>
                      ) : (
                        <Typography variant="body2" color="text.secondary">-</Typography>
                      )}
                    </TableCell>
                    <TableCell align="center" onClick={(e) => e.stopPropagation()}>
                      <Box sx={{ display: 'flex', gap: 0.5, justifyContent: 'center' }}>
                        {item.reply ? (
                          <Tooltip title={`Replied by ${item.replied_by_username || 'Admin'} on ${item.reply_sent_at ? formatDate(item.reply_sent_at) : 'N/A'}`}>
                            <IconButton
                              size="small"
                              color="success"
                              onClick={() => handleOpenReplyDialog(item)}
                            >
                              <CheckCircleIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        ) : (
                          <Tooltip title="Reply to feedback">
                            <IconButton
                              size="small"
                              color="primary"
                              onClick={() => handleOpenReplyDialog(item)}
                            >
                              <ReplyIcon fontSize="small" />
                            </IconButton>
                          </Tooltip>
                        )}
                        <Tooltip title={item.forwards_count > 0 ? `Forwarded ${item.forwards_count} time(s)` : 'Forward to department'}>
                          <IconButton
                            size="small"
                            color={item.forwards_count > 0 ? 'success' : 'primary'}
                            onClick={() => handleOpenForwardDialog(item)}
                          >
                            {item.forwards_count > 0 ? (
                              <Box sx={{ position: 'relative' }}>
                                <ForwardIcon fontSize="small" />
                                <Chip
                                  label={item.forwards_count}
                                  size="small"
                                  color="success"
                                  sx={{
                                    position: 'absolute',
                                    top: -8,
                                    right: -8,
                                    minWidth: 16,
                                    height: 16,
                                    fontSize: '0.65rem',
                                    '& .MuiChip-label': { px: 0.3 }
                                  }}
                                />
                              </Box>
                            ) : (
                              <ForwardIcon fontSize="small" />
                            )}
                          </IconButton>
                        </Tooltip>
                      </Box>
                    </TableCell>
                  </TableRow>
                ))
              )}
            </TableBody>
          </Table>
        </TableContainer>
        
        <TablePagination
          rowsPerPageOptions={[5, 10, 25, 50]}
          component="div"
          count={totalCount}
          rowsPerPage={rowsPerPage}
          page={page}
          onPageChange={handleChangePage}
          onRowsPerPageChange={handleChangeRowsPerPage}
        />
      </Paper>
      
      {/* Reply Dialog */}
      <Dialog
        open={replyDialogOpen}
        onClose={handleCloseReplyDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          {selectedFeedback?.reply ? 'View/Edit Reply' : 'Reply to Feedback'}
        </DialogTitle>
        <DialogContent>
          {selectedFeedback && (
            <Box sx={{ mb: 3, mt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Guest: {selectedFeedback.guest_name}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Property: {selectedFeedback.property_name}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Rating: {selectedFeedback.overall_rating}/5
              </Typography>
              
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Customer Feedback:
                </Typography>
                <Typography variant="body2">
                  {selectedFeedback.comments || 'No comments provided'}
                </Typography>
              </Box>
              
              {selectedFeedback.reply && (
                <Box sx={{ mt: 2, p: 2, bgcolor: 'success.50', borderRadius: 1 }}>
                  <Typography variant="subtitle2" color="success.main" gutterBottom>
                    Previous Reply by {selectedFeedback.replied_by_username || 'Admin'}:
                  </Typography>
                  <Typography variant="body2">
                    {selectedFeedback.reply}
                  </Typography>
                  <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
                    Sent: {selectedFeedback.reply_sent_at ? formatDate(selectedFeedback.reply_sent_at) : 'N/A'}
                  </Typography>
                </Box>
              )}
              
              <TextField
                label="Your Reply"
                multiline
                rows={6}
                fullWidth
                value={replyText}
                onChange={(e) => setReplyText(e.target.value)}
                placeholder="Type your reply to the customer here..."
                sx={{ mt: 3 }}
                helperText="This reply will be sent to the customer via email"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseReplyDialog} disabled={replySending}>
            Cancel
          </Button>
          <Button
            onClick={handleSendReply}
            variant="contained"
            disabled={replySending || !replyText.trim()}
            startIcon={replySending ? <CircularProgress size={20} /> : <ReplyIcon />}
          >
            {replySending ? 'Sending...' : 'Send Reply'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Forward Dialog */}
      <Dialog
        open={forwardDialogOpen}
        onClose={handleCloseForwardDialog}
        maxWidth="md"
        fullWidth
      >
        <DialogTitle>
          Forward Feedback to Department
        </DialogTitle>
        <DialogContent>
          {selectedFeedback && (
            <Box sx={{ mb: 3, mt: 2 }}>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Guest: {selectedFeedback.guest_name}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Property: {selectedFeedback.property_name}
              </Typography>
              <Typography variant="subtitle2" color="text.secondary" gutterBottom>
                Rating: {selectedFeedback.overall_rating}/5
              </Typography>
              
              <Box sx={{ mt: 2, p: 2, bgcolor: 'grey.100', borderRadius: 1 }}>
                <Typography variant="subtitle2" gutterBottom>
                  Customer Feedback:
                </Typography>
                <Typography variant="body2">
                  {selectedFeedback.comments || 'No comments provided'}
                </Typography>
              </Box>
              
              {/* Show forward history if exists */}
              {selectedFeedback.forwards && selectedFeedback.forwards.length > 0 && (
                <Box sx={{ mt: 2 }}>
                  <Button
                    size="small"
                    startIcon={<HistoryIcon />}
                    onClick={() => setShowForwardHistory(!showForwardHistory)}
                  >
                    {showForwardHistory ? 'Hide' : 'Show'} Forward History ({selectedFeedback.forwards.length})
                  </Button>
                  
                  {showForwardHistory && (
                    <Box sx={{ mt: 2, maxHeight: 200, overflowY: 'auto' }}>
                      {selectedFeedback.forwards.map((forward: FeedbackForward) => (
                        <Box key={forward.id} sx={{ p: 2, mb: 1, bgcolor: 'info.50', borderRadius: 1 }}>
                          <Typography variant="caption" color="text.secondary">
                            Forwarded to: <strong>{forward.forwarded_to_email}</strong>
                          </Typography>
                          <Typography variant="caption" color="text.secondary" display="block">
                            By: {forward.forwarded_by_username || 'Admin'} on {formatDate(forward.forwarded_at)}
                          </Typography>
                          {forward.comments && (
                            <Typography variant="body2" sx={{ mt: 1 }}>
                              Comments: {forward.comments}
                            </Typography>
                          )}
                        </Box>
                      ))}
                    </Box>
                  )}
                </Box>
              )}
              
              <TextField
                label="Department Email"
                type="email"
                fullWidth
                value={forwardEmail}
                onChange={(e) => setForwardEmail(e.target.value)}
                placeholder="department@example.com"
                sx={{ mt: 3 }}
                required
                helperText="Email address of the department to forward this feedback to"
              />
              
              <TextField
                label="Comments (Optional)"
                multiline
                rows={4}
                fullWidth
                value={forwardComments}
                onChange={(e) => setForwardComments(e.target.value)}
                placeholder="Add any comments or notes for the department..."
                sx={{ mt: 2 }}
                helperText="Optional comments that will be included in the forwarded email"
              />
            </Box>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseForwardDialog} disabled={forwardSending}>
            Cancel
          </Button>
          <Button
            onClick={handleSendForward}
            variant="contained"
            disabled={forwardSending || !forwardEmail.trim()}
            startIcon={forwardSending ? <CircularProgress size={20} /> : <ForwardIcon />}
          >
            {forwardSending ? 'Sending...' : 'Forward'}
          </Button>
        </DialogActions>
      </Dialog>
      
      {/* Snackbar for notifications */}
      <Snackbar
        open={snackbarOpen}
        autoHideDuration={6000}
        onClose={handleCloseSnackbar}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleCloseSnackbar} severity={snackbarSeverity} sx={{ width: '100%' }}>
          {snackbarMessage}
        </Alert>
      </Snackbar>
    </Box>
  );
};

export default FeedbackList; 